export function createWebDatabase() {
  // Mock database that provides the same interface but doesn't actually persist data.
  // Web doesn't have expo-sqlite support; every chain is a no-op that returns empty
  // results, so any consumer (durableCache, downloadManager etc.) can call the full
  // drizzle API surface without throwing.
  //
  // All terminal methods (`run`, `all`, `get`, `returning`) resolve via either an
  // empty array (selects) or a default insert/update result (writes). Promise-shaped
  // methods like `onConflictDoUpdate` resolve immediately so async callers don't hang.
  const conflictWrite = () =>
    Object.assign(Promise.resolve({ lastInsertRowId: 1, changes: 0 }), {
      run: () => ({ lastInsertRowId: 1, changes: 0 }),
    });

  const insertChain: any = {
    values: () => insertChain,
    onConflictDoUpdate: conflictWrite,
    onConflictDoNothing: conflictWrite,
    returning: () => [],
    run: () => ({ lastInsertRowId: 1, changes: 1 }),
  };
  const selectChain: any = {
    from: () => selectChain,
    where: () => selectChain,
    orderBy: () => selectChain,
    limit: () => selectChain,
    offset: () => selectChain,
    innerJoin: () => selectChain,
    leftJoin: () => selectChain,
    groupBy: () => selectChain,
    all: () => [],
    get: () => undefined,
    run: () => ({ changes: 0 }),
  };
  const updateChain: any = {
    set: () => updateChain,
    where: () => updateChain,
    returning: () => [],
    run: () => ({ changes: 1 }),
  };
  const deleteChain: any = {
    where: () => deleteChain,
    returning: () => [],
    run: () => ({ changes: 1 }),
  };

  type WebDb = {
    select: () => any;
    insert: () => any;
    update: () => any;
    delete: () => any;
    transaction: (fn: (tx: WebDb) => unknown) => Promise<unknown>;
    query: Record<string, unknown>;
  };

  const db: WebDb = {
    select: () => selectChain,
    insert: () => insertChain,
    update: () => updateChain,
    delete: () => deleteChain,
    transaction: async (fn) => fn(db),
    // Mock query method for drizzle's relational queries.
    query: {},
  };

  // Add query methods for each table that might be used
  // This provides a basic interface that won't break the app on web
  const tables = [
    "downloadQueue",
    "downloads",
    "trackCache",
    "offlineData",
    "offlineAccessLeases",
    "migrations",
  ];

  tables.forEach((tableName) => {
    if (db.query) {
      (db.query as any)[tableName] = {
        findFirst: () => Promise.resolve(null),
        findMany: () => Promise.resolve([]),
      };
    }
  });

  return db;
}
